/* WoahNa Fluid 1.0 — native Shopify forms first, progressive interaction second. */
(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const all = (s, context = document) => [...context.querySelectorAll(s)];
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const announce = message => { const el = document.querySelector('[data-global-status]'); if (el) el.textContent = message; };
  const drawers = new WeakMap();
  class Drawer {
    constructor(el) {
      this.el = el; this.x = 0; this.v = 0; this.target = 0; this.raf = 0; this.drag = null;
      el.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      el.addEventListener('click', event => { if (event.target === el && event.clientX < el.getBoundingClientRect().left) this.close(); });
      const handle = el.querySelector('[data-drag-handle]');
      if (!handle) return;
      handle.addEventListener('pointerdown', event => {
        if (event.button !== 0 || event.target.closest('button,a,input')) return;
        this.drag = { id: event.pointerId, start: event.clientX, y: event.clientY, active: false, history: [[event.clientX, event.timeStamp]] };
      });
      handle.addEventListener('pointermove', event => {
        const drag = this.drag; if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.start, dy = event.clientY - drag.y;
        if (!drag.active && Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
        if (!drag.active && Math.abs(dx) < 10) return;
        if (!drag.active) {
          cancelAnimationFrame(this.raf); drag.origin = this.x - dx; drag.active = true; handle.setPointerCapture(event.pointerId);
        }
        const w = el.offsetWidth, raw = drag.origin + dx;
        const rubber = overshoot => overshoot * w * .55 / (w + .55 * Math.abs(overshoot));
        this.x = raw < 0 ? rubber(raw) : raw > w ? w + rubber(raw - w) : raw;
        this.paint(); drag.history.push([event.clientX, event.timeStamp]);
        drag.history = drag.history.filter(p => event.timeStamp - p[1] < 100);
      });
      const release = (event, cancelled = false) => {
        const drag = this.drag; if (!drag || event.pointerId !== drag.id) return;
        if (drag.active) {
          drag.history.push([event.clientX, event.timeStamp]);
          const recent = drag.history.filter(p => event.timeStamp - p[1] < 100);
          const first = recent[0], last = recent[recent.length - 1];
          const velocity = first && last[1] > first[1] ? (last[0] - first[0]) / (last[1] - first[1]) * 1000 : 0;
          const projected = this.x + velocity / 1000 * .998 / (1 - .998), w = el.offsetWidth;
          const target = cancelled ? 0 : Math.abs(velocity) > 80 ? (velocity > 0 && projected > w / 2 ? w : 0) : (this.x > w / 2 ? w : 0);
          this.spring(target, cancelled ? 0 : velocity, true);
          if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
        }
        this.drag = null;
      };
      handle.addEventListener('pointerup', event => release(event));
      handle.addEventListener('pointercancel', event => release(event, true));
    }
    paint() { this.el.style.transform = `translateX(${this.x}px)`; }
    spring(target, velocity = this.v, momentum = false) {
      cancelAnimationFrame(this.raf); this.target = target; this.v = velocity;
      if (reduced()) {
        const opacity = this.fade ? getComputedStyle(this.el).opacity : target === 0 ? '0' : '1';
        this.fade?.cancel(); this.x = 0; this.paint();
        if (typeof this.el.animate !== 'function') { this.finish(); return; }
        const fade = this.el.animate([{opacity}, {opacity: target === 0 ? 1 : 0}], {duration: 100, fill: 'both'});
        this.fade = fade;
        fade.finished.then(() => { if (this.fade === fade) { this.fade = null; fade.cancel(); this.finish(); } }).catch(() => {});
        return;
      }
      let last = 0; const omega = 22, damping = momentum ? .8 : 1;
      const tick = time => {
        const dt = Math.min((time - (last || time - 16)) / 1000, .032); last = time;
        for (let i = 0; i < 4; i++) { const step = dt / 4; this.v += (-omega * omega * (this.x - this.target) - 2 * damping * omega * this.v) * step; this.x += this.v * step; }
        this.paint();
        if (Math.abs(this.x - this.target) < .25 && Math.abs(this.v) < 2) { this.x = this.target; this.paint(); this.finish(); }
        else this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
    finish() {
      if (this.target > 0 && this.el.open) {
        this.el.close(); this.el.style.willChange = '';
        if (!document.querySelector('dialog[open]')) document.body.style.overflow = '';
        if (this.returnFocus?.isConnected) this.returnFocus.focus();
      }
    }
    open(trigger) {
      if (!this.el.open) { this.returnFocus = trigger || document.activeElement; this.el.showModal(); this.x = this.el.offsetWidth; this.v = 0; this.paint(); }
      this.el.style.willChange = 'transform'; document.body.style.overflow = 'hidden'; this.spring(0);
    }
    close() { this.el.style.willChange = 'transform'; this.spring(this.el.offsetWidth); }
  }
  function drawer(el) { if (!drawers.has(el)) drawers.set(el, new Drawer(el)); return drawers.get(el); }
  async function request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let data; try { data = await response.json(); } catch { throw new Error('We couldn’t confirm the update. Check your bag before trying again.'); }
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Shopify couldn’t update your bag. Please review your selection.');
    return data;
  }
  function applyCartHTML(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html'), incoming = doc.querySelector('[data-cart-body]');
    if (!incoming) return false;
    const target = document.querySelector('#WnCart [data-cart-body]');
    if (!target) return false;
    target.replaceWith(incoming);
    all('[data-cart-count]').forEach(el => { el.textContent = incoming.dataset.itemCount; });
    return true;
  }
  let cartRefresh = 0;
  async function refreshCart(sections) {
    if (sections?.['wn-cart-panel'] && applyCartHTML(sections['wn-cart-panel'])) return;
    const serial = ++cartRefresh;
    const response = await fetch(root + 'cart?section_id=wn-cart-panel', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh the bag. Open the full bag to check your items.');
    const html = await response.text();
    if (serial !== cartRefresh) return;
    if (!applyCartHTML(html)) throw new Error('Unable to refresh the bag. Open the full bag to check your items.');
  }
  async function openCart(trigger) {
    const el = document.getElementById('WnCart'); if (!el || typeof el.showModal !== 'function') { location.assign(root + 'cart'); return; }
    drawer(el).open(trigger);
    const status = el.querySelector('[data-cart-status]'); if (status) status.textContent = 'Updating your bag…';
    try { await refreshCart(); } catch (error) { showCartError(error.message); }
  }
  function showCartError(message) {
    const status = document.querySelector('#WnCart [data-cart-status]');
    if (!status) return;
    status.textContent = message + ' ';
    const link = document.createElement('a'); link.href = root + 'cart'; link.className = 'textlink'; link.textContent = 'View full bag →'; status.append(link);
  }
  const initialized = new WeakSet();
  function initProduct(el) {
    if (initialized.has(el)) return;
    const script = el.querySelector('[data-product-data]'), form = el.querySelector('.wn-product-form');
    if (!script || !form) return;
    let data; try { data = JSON.parse(script.textContent); } catch { return; }
    const select = el.querySelector('[data-variant-select]'), add = el.querySelector('[data-add]'), status = el.querySelector('[data-product-status]');
    if (!select || !add || !data.variants?.length) return;
    initialized.add(el); let selected = data.variants.find(v => String(v.id) === select.value) || data.variants[0];
    let options = [...selected.options], busy = false;
    const variants = data.variants;
    el.querySelector('[data-native-variants]').hidden = true; el.querySelector('[data-enhanced-options]').hidden = false;
    function updateVariant(updateUrl = true) {
      selected = variants.find(v => v.options.every((value, index) => value === options[index]));
      select.value = selected ? String(selected.id) : '';
      all('[data-option-index]', el).forEach(group => {
        const index = Number(group.dataset.optionIndex); group.querySelector('[data-option-label]').textContent = options[index] || '';
        all('[data-option-value]', group).forEach(button => {
          const value = button.dataset.optionValue, active = options[index] === value;
          button.classList.toggle('selected', active); button.setAttribute('aria-pressed', String(active));
          // Each later option is constrained by earlier options; color remains changeable.
          const available = variants.some(v => v.available && v.options[index] === value && v.options.slice(0, index).every((val, idx) => val === options[idx]));
          button.classList.toggle('unavailable', !available); button.setAttribute('aria-label', value + (available ? '' : ' — unavailable'));
        });
      });
      add.disabled = busy || !selected?.available; add.textContent = busy ? 'Adding…' : !selected ? 'Unavailable combination' : selected.available ? 'Add to bag' : 'Sold out';
      if (selected) {
        el.querySelector('[data-product-price]').textContent = selected.price;
        const compare = el.querySelector('[data-compare-price]'); compare.hidden = !selected.compare; compare.textContent = selected.compare || '';
        if (selected.image) setImage(selected.image, data.title + ' — ' + options.join(' / '));
        if (updateUrl) { const url = new URL(location.href); url.searchParams.set('variant', selected.id); history.replaceState({}, '', url); }
      }
      if (!selected) status.textContent = 'That combination is unavailable. Choose another option.';
      else status.textContent = selected.available ? '' : 'This option is currently sold out.';
    }
    function setImage(src, alt) {
      const image = el.querySelector('[data-product-image]'); if (!image) return;
      image.removeAttribute('srcset'); image.src = src; image.alt = alt;
      all('[data-image-url]', el).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.imageUrl === src)));
    }
    el.addEventListener('click', event => {
      const button = event.target.closest('[data-option-value]');
      if (button) { const index = Number(button.closest('[data-option-index]').dataset.optionIndex); options[index] = button.dataset.optionValue; updateVariant(); }
      const thumb = event.target.closest('[data-image-url]'); if (thumb) setImage(thumb.dataset.imageUrl, thumb.dataset.imageAlt);
    });
    form.addEventListener('submit', async event => {
      if (event.submitter?.name !== 'add' && event.submitter) return;
      event.preventDefault(); if (busy || !selected?.available || !form.reportValidity()) return;
      busy = true; add.disabled = true; add.textContent = 'Adding…'; status.textContent = 'Adding your selection to the bag…';
      const body = new FormData(form); body.set('sections', 'wn-cart-panel'); body.set('sections_url', root + 'cart');
      let added = false;
      try {
        const response = await request(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body });
        added = true; ++cartRefresh;
        status.textContent = 'Added to your bag.'; announce('Added to your bag.');
        const dialog = document.getElementById('WnCart');
        if (dialog && typeof dialog.showModal === 'function') {
          drawer(dialog).open(add);
          try { await refreshCart(response.sections); } catch (error) { showCartError('Added to your bag. ' + error.message); }
        } else location.assign(root + 'cart');
      } catch (error) { status.textContent = error.message || 'We couldn’t confirm the update. Check your bag before trying again.'; }
      finally { busy = false; add.disabled = !selected?.available; add.textContent = selected?.available ? 'Add to bag' : 'Sold out'; if (added) status.textContent = 'Added to your bag.'; }
    });
    const wantedColor = new URLSearchParams(location.search).get('color');
    if (wantedColor) { const colorGroup = all('[data-option-index]', el).find(group => all('[data-option-value]', group).some(b => b.dataset.optionValue.toLowerCase() === wantedColor.toLowerCase())); if (colorGroup) { const b = all('[data-option-value]', colorGroup).find(b => b.dataset.optionValue.toLowerCase() === wantedColor.toLowerCase()); options[Number(colorGroup.dataset.optionIndex)] = b.dataset.optionValue; } }
    updateVariant(false);
  }
  let cartBusy = false;
  async function changeCart(key, quantity, trigger) {
    if (cartBusy) return;
    if (!Number.isInteger(quantity) || quantity < 0) { showCartError('Enter a whole-number quantity of zero or more.'); return; }
    cartBusy = true; ++cartRefresh;
    const body = document.querySelector('#WnCart [data-cart-body]');
    body?.setAttribute('aria-busy', 'true'); all('input,button', body || document).forEach(el => el.disabled = true);
    try {
      const response = await request(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: ['wn-cart-panel'], sections_url: root + 'cart' }) });
      await refreshCart(response.sections); announce('Bag updated.');
      const next = all('#WnCart [data-line-key]').find(el => el.dataset.lineKey === key);
      (next || document.querySelector('#WnCart [data-close-dialog]'))?.focus();
    } catch (error) { showCartError(error.message); }
    finally { cartBusy = false; const current = document.querySelector('#WnCart [data-cart-body]'); current?.removeAttribute('aria-busy'); all('input,button', current || document).forEach(el => el.disabled = false); }
  }
  document.addEventListener('click', event => {
    const bag = event.target.closest('[data-open-cart]');
    if (bag && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); openCart(bag); return; }
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) { const el = document.getElementById(opener.dataset.openDialog); if (el?.showModal) drawer(el).open(opener); }
    const close = event.target.closest('[data-close-dialog]'); if (close) drawer(close.closest('dialog')).close();
    const remove = event.target.closest('#WnCart [data-remove-key]'); if (remove) { event.preventDefault(); changeCart(remove.dataset.removeKey, 0, remove); }
  });
  document.addEventListener('change', event => { if (event.target.matches('#WnCart [data-line-key]')) changeCart(event.target.dataset.lineKey, Number(event.target.value), event.target); });
  document.addEventListener('submit', event => {
    const cartForm = event.target.closest('#WnCart [data-cart-form]');
    if (!cartForm) return;
    if (cartBusy) { event.preventDefault(); return; }
    // Checkout and the no-JS update action remain native Shopify form submissions.
  });
  function init(scope = document) {
    all('[data-product-section]', scope).forEach(initProduct);
    all('[data-faq]', scope).forEach(el => {
      if (initialized.has(el)) return; initialized.add(el);
      const input = el.querySelector('[data-faq-search]'); if (!input) return;
      input.closest('label').hidden = false;
      input.addEventListener('input', () => { let matches = 0; all('[data-faq-item]', el).forEach(item => { item.hidden = !item.textContent.toLowerCase().includes(input.value.toLowerCase()); if (!item.hidden) matches++; }); el.querySelector('[data-faq-empty]').hidden = matches > 0; });
    });
    all('input[required],textarea[required]', scope).forEach(input => {
      if (initialized.has(input)) return; initialized.add(input);
      input.addEventListener('blur', () => { if (input.value) input.setAttribute('aria-invalid', String(!input.validity.valid)); });
      input.addEventListener('input', () => { if (input.hasAttribute('aria-invalid')) input.setAttribute('aria-invalid', String(!input.validity.valid)); });
    });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { all('dialog', event.target).forEach(el => { const instance = drawers.get(el); if (instance) cancelAnimationFrame(instance.raf); }); document.body.style.overflow = ''; });
  init();
})();
